import {Database} from '@tnc/shared';

import {sortOptionT} from '@/lib/types/dataTable.types';

/**
 * All sort options for the database tables in a key-value pair
 */
type sortOptionsT = {
  [K in keyof Database['public']['Tables']]?: sortOptionT<K>[];
};

/**
 * A constant for all sort options for the database tables
 *
 * @example
 * SORT_OPTIONS['Brand'] returns the sort options for the Brand table
 */
export const SORT_OPTIONS: sortOptionsT = {
  Brand: [
    {
      label: 'Name',
      column: 'brand_name',
      direction: 'asc',
      default: true,
    },
    {
      label: 'Brand City',
      column: 'brand_city',
      direction: 'asc',
    },
  ],
  Distributor: [
    {
      label: 'Name',
      column: 'distributor_name',
      direction: 'asc',
      default: true,
    },
    {
      label: 'City',
      column: 'distributor_city',
      direction: 'asc',
    },
  ],
  Admin: [
    {
      label: 'Admin Id',
      column: 'admin_id',
      direction: 'asc',
      default: true,
    },
  ],
  Retailer: [
    {
      label: 'City',
      column: 'retailer_city',
      direction: 'asc',
      default: true,
    },
    {
      label: 'Name',
      column: 'retailer_name',
      direction: 'asc',
    },
  ],
  Position: [
    {
      label: 'Status',
      column: 'position_status',
      direction: 'asc',
    },
    {
      label: 'Position Id',
      column: 'position_id',
      direction: 'asc',
      default: true,
    },
    {
      label: 'Speculator',
      column: 'fk_speculator_id',
      direction: 'asc',
    },
  ],
  Product: [
    {
      label: 'Name',
      column: 'product_name',
      direction: 'asc',
      default: true,
    },
    {
      label: 'Category',
      column: 'product_category',
      direction: 'asc',
    },
    {
      label: 'Subcategory',
      column: 'product_subcategory',
      direction: 'asc',
    },
  ],
  Speculator: [
    {
      label: 'Id',
      column: 'speculator_id',
      direction: 'asc',
      default: true,
    },
    {
      label: 'Username',
      column: 'speculator_username',
      direction: 'asc',
    },
    {
      label: 'Status',
      column: 'speculator_status',
      direction: 'asc',
    },
  ],
  // Add more tables here. Markets, Distributors, etc.
  // ex:
  // Market: [
  //   {
  //     label: 'Name',
  //     column: 'market_name',
  //     direction: 'asc',
  //     default: true
  //   },
  //   {
  //     label: 'Recently Modified',
  //     column: 'date_modified',
  //     direction: 'asc',
  //   },
  // ],
} as const;

// Helper function to get sort options for a given table
export const getSortOptions = <T extends keyof Database['public']['Tables']>(table: T) => {
  const tableSortOptions = SORT_OPTIONS[table] || [];

  // Check if 'New' and 'Old' options already exist
  const hasNewOption = tableSortOptions.some(option => option.label === 'New' && option.column === 'date_created');
  const hasOldOption = tableSortOptions.some(option => option.label === 'Old' && option.column === 'date_created');

  // Created date
  const dateCreatedColumn = 'date_created' as keyof Database['public']['Tables'][T]['Row'];

  // Only add default options if they don't exist
  const additionalOptions: sortOptionT<T>[] = [];

  if (!hasNewOption) {
    additionalOptions.push({
      label: 'New',
      column: dateCreatedColumn,
      direction: 'desc',
    });
  }
  if (!hasOldOption) {
    additionalOptions.push({
      label: 'Old',
      column: dateCreatedColumn,
      direction: 'asc',
    });
  }

  // Combine options
  // Get the sort options for the given table if they exist, and add the default sort options,
  // otherwise return the default sort options
  return [...tableSortOptions, ...additionalOptions];
};
